/**
 * Copilot — operador cognitivo contextual de la Capa Operacional AI-First
 * (variante **demo** client-side, R6).
 *
 * Implementa la interfaz {@link Copilot} de `../api/contracts` con un
 * **planner determinista por intents** (sin LLM por defecto): dado un texto
 * de intención y el {@link ContextoIA} ensamblado por el Motor_Contexto_IA
 * (única puerta de lectura, R6.5), produce un {@link Plan} de uno de cinco
 * tipos (R6):
 *
 *   - **NavegarEntidad** — intención de navegación contextual; se resuelve a
 *     **exactamente una** entidad del Grafo_Entidades presente en el contexto
 *     y enfoca la vista correspondiente (R6.1, Property 21).
 *   - **ResponderCausaRaiz** — consulta de causa raíz; responde usando los
 *     Insights aplicables y su Cadena_Causal del Motor_Insights, **citando al
 *     menos un elemento de evidencia** (R6.2, Property 23). Si no existe
 *     ningún Insight aplicable, **informa la ausencia** y **NO fabrica** una
 *     respuesta causal (R6.9).
 *   - **ConsultaGeoOperacional** — consulta geoespacial/operacional (ej.
 *     "¿Qué válvulas debo cerrar?"); consulta el Grafo_Entidades y presenta
 *     el resultado con las entidades consultadas y el razonamiento (R6.3).
 *   - **AccionMultiEntidad** — operación sobre múltiples entidades; resuelve
 *     el conjunto objetivo y **lista** las acciones, pero **NO invoca** ninguna
 *     Accion_Semantica hasta recibir confirmación humana explícita (R6.4); la
 *     propuesta pendiente **expira** sin confirmación en una ventana
 *     configurable (default 300 s, R6.10).
 *   - **Aclarar** — intención ambigua, no resoluble a una entidad/acción
 *     concreta o con múltiples candidatos; el Copilot **pide aclaración sin
 *     ejecutar ninguna acción**, preservando el estado (R6.7).
 *
 * Toda respuesta adjunta su {@link ExplainabilityCopilot} (entidades
 * consultadas, evidencia y razonamiento) para permitir verificación humana
 * (R6.8). El Copilot **solo** lee contexto vía el Motor_Contexto_IA y **solo**
 * ejecuta acciones invocando el Registro_Acciones_Semanticas
 * (`runtime.registry()`), respetando permisos y políticas (R6.5, R6.6).
 *
 * Determinismo y testabilidad: la clasificación de intención y la resolución
 * de entidades/insights son **funciones puras** exportadas
 * ({@link clasificarIntencion}, {@link resolverCandidatosEntidad},
 * {@link interpretarPlan}), de modo que las Properties 21 y 23 puedan
 * ejercitarlas sin instanciar la clase ni mockear. La expiración no depende de
 * `setTimeout`: usa un **reloj inyectable** y un chequeo por timestamp.
 *
 * LLM opcional detrás del **mismo contrato**: si se configura un proveedor LLM
 * (`VITE_ANC_LLM_*`) se podría delegar la interpretación en él produciendo el
 * mismo tipo de {@link Plan}; en ausencia de configuración (caso por defecto)
 * el planner es **100% determinista**.
 *
 * Frontera arquitectónica: este módulo consume el Registro y el
 * Motor_Contexto_IA **exclusivamente vía el runtime selector**
 * (`runtime.registry()`, `runtime.contextoIA()`), nunca instanciándolos
 * directamente, y se **autorregistra** como proveedor DEMO del servicio
 * `copilot` al importarse, siguiendo el patrón de `contextoIA`/`insightsRuntime`
 * (no toca `demoRuntime.ts`).
 */

import type {
  AccionSugerida,
  ContextoIA,
  Copilot,
  EntidadRef,
  ExplainabilityCopilot,
  Id,
  Insight,
  IsoTimestamp,
  Nodo,
  ParamsAccion,
  Plan,
  PropuestaPendiente,
  Result,
  ResultadoAccion,
  TipoEntidad,
} from "../api/contracts";
import { provideDemo, runtime } from "../api/runtime";
import {
  ACCION_ABRIR_ORDEN_INSPECCION,
  ACCION_CERRAR_VALVULA,
} from "./actionCatalog";

// ---------------------------------------------------------------------------
// Configuración
// ---------------------------------------------------------------------------

/** Ventana por defecto de validez de una propuesta multi-entidad (R6.10): 300 s. */
export const VENTANA_CONFIRMACION_MS_DEFAULT = 300 * 1000;

/** Versión de código del planner (paridad determinista demo/conectado). */
export const COPILOT_CODE_VERSION = "ai-operational-layer/copilot@6.16";

// ---------------------------------------------------------------------------
// Clasificación de intención (función pura, determinista) — R6.1–R6.4, R6.7
// ---------------------------------------------------------------------------

/** Tipo de intención reconocida por el planner. */
export type TipoIntencion =
  | "navegacion"
  | "causa_raiz"
  | "geo_operacional"
  | "accion_multi"
  | "ambigua";

/** Normaliza un texto a minúsculas sin acentos, para coincidencias estables. */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/** Marcadores léxicos (ya normalizados) de cada intención. */
const MARCADORES_CAUSA_RAIZ = [
  "por que",
  "porque",
  "causa",
  "causo",
  "razon",
  "motivo",
  "a que se debe",
  "explica por",
  "por cual",
];

const MARCADORES_GEO = [
  "valvula",
  "valvulas",
  "aislar",
  "aislamiento",
  "aguas abajo",
  "downstream",
  "cerrar",
  "abrir valvula",
  "setpoint",
  "prv",
];

const MARCADORES_BULK = [
  "todos",
  "todas",
  "cada",
  "masiv",
  "en bloque",
  "para todos",
  "para todas",
  "multiples",
  "varios",
  "varias",
];

const MARCADORES_ACCION = [
  "crear",
  "crea",
  "genera",
  "generar",
  "abrir orden",
  "abre orden",
  "asignar",
  "asigna",
  "notificar",
  "notifica",
  "programar",
  "programa",
  "despachar",
  "despacha",
];

const MARCADORES_NAVEGACION = [
  "muestrame",
  "muestra",
  "ver ",
  "abre ",
  "abrir ",
  "navega",
  "ir a",
  "enfoca",
  "focaliza",
  "selecciona",
  "llevame a",
  "mostrar",
];

function contieneAlguno(texto: string, marcadores: readonly string[]): boolean {
  return marcadores.some((m) => texto.includes(m));
}

/**
 * Clasifica la intención de un texto de forma **determinista** (R6.1–R6.4,
 * R6.7). El orden de evaluación resuelve solapamientos: acción multi-entidad
 * (verbo de acción + marcador de conjunto) ▸ causa raíz ▸ geo/operacional ▸
 * navegación ▸ ambigua. Función pura: el mismo texto produce siempre la misma
 * clasificación.
 */
export function clasificarIntencion(texto: string): TipoIntencion {
  const t = normalizar(texto);
  if (t.length === 0) return "ambigua";

  const tieneAccion = contieneAlguno(t, MARCADORES_ACCION);
  const tieneBulk = contieneAlguno(t, MARCADORES_BULK);
  if (tieneAccion && tieneBulk) return "accion_multi";

  if (contieneAlguno(t, MARCADORES_CAUSA_RAIZ)) return "causa_raiz";

  if (contieneAlguno(t, MARCADORES_GEO)) return "geo_operacional";

  if (contieneAlguno(t, MARCADORES_NAVEGACION)) return "navegacion";

  return "ambigua";
}

// ---------------------------------------------------------------------------
// Resolución de entidades del contexto (función pura) — R6.1, R6.7
// ---------------------------------------------------------------------------

/** Clave canónica de una referencia de entidad. */
function refKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/** Escapa una cadena para usarla literalmente dentro de una expresión regular. */
function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Resuelve los **candidatos de entidad** del contexto que aparecen
 * mencionados en el texto, de forma determinista (R6.1, R6.7). Un nodo es
 * candidato si el identificador de su entidad (o, en su defecto, su nombre en
 * `attrs.nombre`) aparece como token delimitado en el texto normalizado. El
 * resultado se deduplica por `node_id` y se ordena de forma estable.
 *
 * Es **pura**: solo depende del texto y de las entidades del contexto, que es
 * la única vía de lectura de la IA (R6.5).
 */
export function resolverCandidatosEntidad(texto: string, entidades: Nodo[]): Nodo[] {
  const t = ` ${normalizar(texto)} `;
  const vistos = new Map<Id, Nodo>();

  for (const nodo of entidades) {
    const claves: string[] = [nodo.entidad_ref.id];
    const nombre = nodo.attrs?.nombre;
    if (typeof nombre === "string" && nombre.length > 0) claves.push(nombre);

    for (const clave of claves) {
      const norm = normalizar(String(clave));
      if (norm.length < 2) continue;
      // Coincidencia delimitada por no-alfanumérico para evitar falsos
      // positivos por subcadenas (ej. "dma-3" dentro de "dma-30").
      const patron = new RegExp(`(^|[^a-z0-9])${escaparRegex(norm)}([^a-z0-9]|$)`);
      if (patron.test(t)) {
        if (!vistos.has(nodo.node_id)) vistos.set(nodo.node_id, nodo);
        break;
      }
    }
  }

  return [...vistos.values()].sort((a, b) => (a.node_id < b.node_id ? -1 : a.node_id > b.node_id ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Insights aplicables a una consulta de causa raíz (función pura) — R6.2, R6.9
// ---------------------------------------------------------------------------

/** Palabras vacías que no aportan a la coincidencia de Insights. */
const STOPWORDS = new Set([
  "por",
  "que",
  "el",
  "la",
  "los",
  "las",
  "un",
  "una",
  "de",
  "del",
  "en",
  "se",
  "ha",
  "the",
  "and",
  "aumento",
  "subio",
  "bajo",
  "cual",
  "cuales",
  "como",
  "donde",
  "esta",
  "este",
]);

/** Extrae los tokens significativos (≥3 caracteres, sin stopwords) del texto. */
function tokensSignificativos(texto: string): string[] {
  return normalizar(texto)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w));
}

/**
 * Determina los **Insights aplicables** a una consulta de causa raíz, de forma
 * determinista (R6.2). Un Insight es aplicable si el identificador de alguna de
 * sus entidades aparece en el texto, o si algún token significativo del texto
 * aparece en su `clave_natural` o en la narrativa del hallazgo. Solo se
 * consideran aplicables Insights con **al menos un elemento de evidencia** de
 * soporte, para garantizar la cita exigida por la Property 23.
 *
 * Si el texto no menciona ninguna entidad concreta, se consideran aplicables
 * todos los Insights con evidencia (consulta de causa raíz de alcance global,
 * ej. "¿Por qué aumentó el NRW?"). Función pura.
 */
export function insightsAplicables(texto: string, insights: Insight[]): Insight[] {
  const t = ` ${normalizar(texto)} `;
  const tokens = tokensSignificativos(texto);
  const conEvidencia = insights.filter((i) => Array.isArray(i.evidencia) && i.evidencia.length >= 1);

  const mencionaEntidad = (i: Insight): boolean =>
    i.entidades.some((e) => {
      const norm = normalizar(e.id);
      return norm.length >= 2 && t.includes(norm);
    });

  const coincideTexto = (i: Insight): boolean => {
    const clave = normalizar(i.clave_natural);
    const hallazgo = normalizar(i.narrativa?.hallazgo ?? "");
    return tokens.some((tok) => clave.includes(tok) || hallazgo.includes(tok));
  };

  // 1) Insights cuya entidad se menciona explícitamente en el texto.
  const porEntidad = conEvidencia.filter(mencionaEntidad);
  if (porEntidad.length > 0) {
    return ordenarInsights(porEntidad);
  }

  // 2) Coincidencia temática por tokens (ej. dominio "nrw").
  const porTexto = conEvidencia.filter(coincideTexto);
  if (porTexto.length > 0) {
    return ordenarInsights(porTexto);
  }

  // 3) Consulta de causa raíz sin entidad ni tema específico: aplican todos
  //    los Insights fundamentados (con evidencia).
  return ordenarInsights(conEvidencia);
}

/** Orden total determinista de Insights por `insight_id`. */
function ordenarInsights(insights: Insight[]): Insight[] {
  return [...insights].sort((a, b) => (a.insight_id < b.insight_id ? -1 : a.insight_id > b.insight_id ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Construcción de explainability (R6.8)
// ---------------------------------------------------------------------------

/** Deduplica referencias de entidad con orden total determinista. */
function dedupRefs(refs: EntidadRef[]): EntidadRef[] {
  const vistos = new Map<string, EntidadRef>();
  for (const r of refs) {
    const k = refKey(r);
    if (!vistos.has(k)) vistos.set(k, { tipo: r.tipo, id: r.id });
  }
  return [...vistos.values()].sort((a, b) => (refKey(a) < refKey(b) ? -1 : refKey(a) > refKey(b) ? 1 : 0));
}

/** Construye una Explainability_Copilot a partir de sus partes (R6.8). */
function explainability(
  entidades_consultadas: EntidadRef[],
  evidencia: EntidadRef[],
  razonamiento: string,
): ExplainabilityCopilot {
  return {
    entidades_consultadas: dedupRefs(entidades_consultadas),
    evidencia: dedupRefs(evidencia),
    razonamiento,
  };
}

// ---------------------------------------------------------------------------
// Parámetros del planner puro
// ---------------------------------------------------------------------------

/** Opciones de interpretación deterministas (versionadas para paridad). */
export interface InterpretarOpciones {
  /** Insights disponibles para resolver consultas de causa raíz (R6.2). */
  insights?: Insight[];
}

// ---------------------------------------------------------------------------
// Constructores de Plan por intención (funciones puras) — R6.1–R6.9
// ---------------------------------------------------------------------------

/** Construye el Plan de aclaración (R6.7): no actúa, preserva el estado. */
function planAclarar(razonamiento: string, aclaracion: string, entidades: EntidadRef[] = []): Plan {
  return {
    tipo: "Aclarar",
    aclaracion,
    explainability: explainability(entidades, [], razonamiento),
  };
}

/** Construye el Plan de navegación a exactamente una entidad (R6.1). */
function planNavegacion(texto: string, entidades: Nodo[]): Plan {
  const candidatos = resolverCandidatosEntidad(texto, entidades);

  if (candidatos.length === 1) {
    const nodo = candidatos[0];
    return {
      tipo: "NavegarEntidad",
      entidad: { tipo: nodo.entidad_ref.tipo, id: nodo.entidad_ref.id },
      explainability: explainability(
        [nodo.entidad_ref],
        [],
        `Intención de navegación resuelta a exactamente una entidad del Grafo_Entidades: ${nodo.entidad_ref.tipo} ${nodo.entidad_ref.id}. Se enfoca la vista correspondiente (R6.1).`,
      ),
    };
  }

  if (candidatos.length === 0) {
    return planAclarar(
      "No se pudo resolver la intención de navegación a una entidad concreta del Grafo_Entidades disponible en el contexto (R6.7).",
      "No identifiqué a qué entidad te refieres. ¿Puedes indicar el identificador (por ejemplo, DMA-003)?",
    );
  }

  // Múltiples candidatos → ambiguo: pedir aclaración sin actuar (R6.7).
  const refs = candidatos.map((n) => n.entidad_ref);
  const lista = refs.map((r) => `${r.tipo} ${r.id}`).join(", ");
  return planAclarar(
    `La intención de navegación admite múltiples candidatos (${candidatos.length}); se solicita aclaración sin ejecutar acción (R6.7).`,
    `Hay varias entidades posibles: ${lista}. ¿Cuál quieres ver?`,
    refs,
  );
}

/**
 * Construye el Plan de respuesta de causa raíz (R6.2, R6.9). Usa los Insights
 * aplicables y su Cadena_Causal citando ≥1 evidencia; si no hay Insight
 * aplicable, informa la ausencia sin fabricar respuesta causal.
 */
function planCausaRaiz(texto: string, insights: Insight[]): Plan {
  const aplicables = insightsAplicables(texto, insights);

  if (aplicables.length === 0) {
    // R6.9: informar ausencia, NO fabricar, preservar estado.
    return {
      tipo: "ResponderCausaRaiz",
      explainability: explainability(
        [],
        [],
        "No existe ningún Insight aplicable a la consulta de causa raíz. No se fabrica una respuesta causal; el estado se preserva (R6.9).",
      ),
    };
  }

  const evidencia: EntidadRef[] = [];
  const entidades: EntidadRef[] = [];
  const hallazgos: string[] = [];
  for (const ins of aplicables) {
    evidencia.push(...ins.evidencia);
    entidades.push(...ins.entidades);
    for (const v of ins.cadena_causal?.vinculos ?? []) {
      evidencia.push(v.desde);
    }
    if (ins.narrativa?.hallazgo) hallazgos.push(ins.narrativa.hallazgo);
  }

  const razonamiento =
    `Respuesta basada en ${aplicables.length} Insight(s) del Motor_Insights y su Cadena_Causal, ` +
    `citando ${dedupRefs(evidencia).length} elemento(s) de evidencia de soporte (R6.2). ` +
    hallazgos.join(" ");

  return {
    tipo: "ResponderCausaRaiz",
    explainability: explainability(entidades, evidencia, razonamiento.trim()),
  };
}

/**
 * Construye el Plan de consulta geoespacial/operacional (R6.3). Consulta el
 * Grafo_Entidades (Valvulas/PRVs presentes en el contexto) y presenta el
 * resultado con las entidades consultadas, el razonamiento y, como **lista
 * informativa**, las acciones candidatas — que NO se invocan aquí.
 */
function planGeoOperacional(texto: string, entidades: Nodo[]): Plan {
  const valvulas = entidades.filter((n) => n.tipo === "Valvula");
  const candidatos = resolverCandidatosEntidad(texto, entidades);
  const dmaContexto = candidatos.filter((n) => n.tipo === "DMA").map((n) => n.entidad_ref);

  // Si el contexto no aporta válvulas, se pide aclaración sin inventar (R6.7).
  if (valvulas.length === 0) {
    return planAclarar(
      "La consulta geoespacial requiere Valvulas en el contexto del Grafo_Entidades y no hay ninguna disponible (R6.3/R6.7).",
      "No tengo válvulas en el contexto actual para responder la consulta. ¿Puedes indicar la DMA o zona objetivo?",
      dmaContexto,
    );
  }

  const refsValvulas = valvulas.map((n) => n.entidad_ref);
  const acciones: AccionSugerida[] = refsValvulas.map((ref) => ({
    accion_semantica_id: ACCION_CERRAR_VALVULA,
    razonamiento: `Candidata para aislamiento/operación: cerrar ${ref.tipo} ${ref.id} (consulta geo-operacional, R6.3). Requiere confirmación humana — no se invoca automáticamente.`,
    evidencia: [ref],
  }));

  return {
    tipo: "ConsultaGeoOperacional",
    acciones,
    explainability: explainability(
      [...refsValvulas, ...dmaContexto],
      refsValvulas,
      `Consulta del Grafo_Entidades y del Aislamiento_Valvular: ${valvulas.length} válvula(s) candidata(s) presentadas con su razonamiento (R6.3). Ninguna acción se ejecuta sin confirmación humana.`,
    ),
  };
}

/** Extrae un umbral numérico ("mayor a 85", "> 85", "score 85") del texto. */
function extraerUmbral(texto: string): number | null {
  const t = normalizar(texto);
  const m = /(?:mayor(?:es)?\s+a|>=?|superior(?:es)?\s+a|sobre|score[_\s]?caso\s*>?=?\s*|score\s*>?=?\s*)\s*(\d{1,3}(?:[.,]\d+)?)/.exec(t);
  if (m) {
    const v = Number(m[1].replace(",", "."));
    if (Number.isFinite(v)) return v;
  }
  return null;
}

/** Tipos de entidad que pueden ser objetivo de una acción multi-entidad. */
const TIPOS_OBJETIVO: ReadonlySet<TipoEntidad> = new Set<TipoEntidad>(["Caso", "DMA"]);

/**
 * Construye el Plan de operación multi-entidad (R6.4). Resuelve el conjunto
 * objetivo del contexto (Casos/DMAs), aplicando el umbral de Score_Caso si el
 * texto lo especifica, y **lista** las Acciones_Semanticas que se ejecutarían;
 * NO invoca ninguna acción (eso requiere confirmación humana, R6.4).
 */
function planAccionMulti(texto: string, entidades: Nodo[]): Plan {
  const umbral = extraerUmbral(texto);
  const objetivos = entidades.filter((n) => {
    if (!TIPOS_OBJETIVO.has(n.tipo)) return false;
    if (umbral !== null) {
      const score = typeof n.attrs?.score === "number" ? (n.attrs.score as number) : undefined;
      if (score === undefined || score <= umbral) return false;
    }
    return true;
  });

  if (objetivos.length === 0) {
    return planAclarar(
      "No se pudo resolver un conjunto objetivo no vacío para la operación multi-entidad con los criterios indicados (R6.4/R6.7).",
      umbral !== null
        ? `No encontré entidades con Score_Caso mayor a ${umbral} en el contexto. ¿Ajustamos el criterio?`
        : "No identifiqué el conjunto de entidades objetivo. ¿Puedes precisar el criterio (por ejemplo, casos con Score mayor a 85)?",
    );
  }

  const refs = objetivos.map((n) => n.entidad_ref);
  const acciones: AccionSugerida[] = refs.map((ref) => ({
    accion_semantica_id: ACCION_ABRIR_ORDEN_INSPECCION,
    razonamiento: `Acción propuesta sobre ${ref.tipo} ${ref.id} como parte de una operación multi-entidad${umbral !== null ? ` (Score_Caso > ${umbral})` : ""}. Pendiente de confirmación humana explícita (R6.4).`,
    evidencia: [ref],
  }));

  return {
    tipo: "AccionMultiEntidad",
    acciones,
    explainability: explainability(
      refs,
      refs,
      `Conjunto objetivo resuelto: ${objetivos.length} entidad(es). Se listan ${acciones.length} acción(es); NINGUNA se invoca hasta confirmación humana explícita (R6.4, R6.10).`,
    ),
  };
}

// ---------------------------------------------------------------------------
// Planner determinista puro (R6.1–R6.9)
// ---------------------------------------------------------------------------

/**
 * **Función pura** que interpreta una intención sobre un contexto y produce un
 * {@link Plan} determinista (R6.1–R6.9). No realiza E/S ni invoca acciones;
 * solo lee del `contexto` (única vía de lectura de la IA, R6.5) y de los
 * Insights provistos en `opts`. El mismo `(texto, contexto, insights)` produce
 * siempre el mismo Plan.
 */
export function interpretarPlan(
  texto: string,
  contexto: ContextoIA,
  opts: InterpretarOpciones = {},
): Plan {
  const entidades = contexto.entidades ?? [];
  const insights = opts.insights ?? [];
  const intencion = clasificarIntencion(texto);

  switch (intencion) {
    case "accion_multi":
      return planAccionMulti(texto, entidades);
    case "causa_raiz":
      return planCausaRaiz(texto, insights);
    case "geo_operacional":
      return planGeoOperacional(texto, entidades);
    case "navegacion":
      return planNavegacion(texto, entidades);
    case "ambigua":
    default:
      return planAclarar(
        "La intención del usuario es ambigua o no resoluble a una entidad o Accion_Semantica concreta; se solicita aclaración sin ejecutar acción (R6.7).",
        "No estoy seguro de qué necesitas. ¿Buscas navegar a una entidad, entender una causa raíz, una consulta sobre la red o una operación sobre varias entidades?",
      );
  }
}

// ---------------------------------------------------------------------------
// ¿Hay un proveedor LLM configurado? (R6 — LLM opcional, mismo contrato)
// ---------------------------------------------------------------------------

/**
 * Indica si hay un proveedor LLM configurado vía `VITE_ANC_LLM_*`. En su
 * ausencia (caso por defecto) el planner es **100% determinista**. La
 * integración real del LLM debe producir el mismo tipo de {@link Plan} para
 * conformar el contrato; aquí solo se detecta la configuración.
 */
export function llmConfigurado(): boolean {
  try {
    const env = typeof import.meta !== "undefined" ? import.meta.env : undefined;
    return Boolean(env?.VITE_ANC_LLM_BASE && env?.VITE_ANC_LLM_KEY);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Propuestas pendientes (HITL) — R6.4, R6.10
// ---------------------------------------------------------------------------

/** Acción de una propuesta con sus parámetros de invocación resueltos. */
interface AccionResuelta {
  accion: AccionSugerida;
  params: ParamsAccion;
}

/** Propuesta multi-entidad almacenada para confirmación/expiración. */
interface PropuestaInterna {
  propuesta_id: Id;
  acciones: AccionResuelta[];
  explainability: ExplainabilityCopilot;
  creada_ms: number;
  expira_ms: number;
  estado: "pendiente" | "confirmada" | "cancelada" | "expirada";
}

/** Mapa nombre-de-parámetro de Accion_Semantica → tipo de entidad objetivo. */
const PARAM_POR_TIPO: Partial<Record<TipoEntidad, string>> = {
  DMA: "dma_ref",
  Valvula: "valvula_ref",
  PRV: "prv_ref",
  Cuadrilla: "cuadrilla_ref",
  Orden_Inspeccion: "orden_ref",
};

/** Deriva los parámetros de invocación de una acción a partir de su evidencia. */
function paramsDeAccion(accion: AccionSugerida): ParamsAccion {
  const ref = accion.evidencia[0];
  if (!ref) return {};
  const nombre = PARAM_POR_TIPO[ref.tipo] ?? "dma_ref";
  return { [nombre]: ref.id };
}

/** Genera un identificador único (UUID si está disponible). */
function generarId(prefijo: string): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return `${prefijo}-${c.randomUUID()}`;
  } catch {
    /* fallback abajo */
  }
  return `${prefijo}-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Instante ISO-8601 UTC a partir de milisegundos epoch. */
function isoDe(ms: number): IsoTimestamp {
  return new Date(ms).toISOString();
}

// ---------------------------------------------------------------------------
// Opciones de construcción del Copilot demo
// ---------------------------------------------------------------------------

/** Opciones del {@link CopilotLocal}. */
export interface CopilotLocalOptions {
  /**
   * Proveedor de Insights para resolver consultas de causa raíz (R6.2). Por
   * defecto vacío; el cableado de UI inyecta los Insights derivados de los
   * datos reales vía el Motor_Insights.
   */
  insightsProvider?: () => Promise<Insight[]> | Insight[];
  /** Reloj inyectable en ms epoch (testabilidad de la expiración, R6.10). */
  reloj?: () => number;
  /** Ventana de validez de una propuesta pendiente en ms (default 300 s). */
  ventanaConfirmacionMs?: number;
}

// ---------------------------------------------------------------------------
// CopilotLocal — implementación demo de la interfaz Copilot
// ---------------------------------------------------------------------------

/**
 * Copilot client-side. Interpreta intenciones sobre el contexto del
 * Motor_Contexto_IA produciendo un {@link Plan} determinista, gestiona las
 * propuestas multi-entidad pendientes de confirmación humana y ejecuta las
 * acciones confirmadas **exclusivamente** vía el Registro_Acciones_Semanticas
 * (policy-aware/permission-aware, R6.6).
 */
export class CopilotLocal implements Copilot {
  private readonly insightsProvider: () => Promise<Insight[]> | Insight[];
  private readonly reloj: () => number;
  private readonly ventanaConfirmacionMs: number;
  private readonly propuestas = new Map<Id, PropuestaInterna>();

  constructor(opts: CopilotLocalOptions = {}) {
    this.insightsProvider = opts.insightsProvider ?? (() => []);
    this.reloj = opts.reloj ?? (() => Date.now());
    this.ventanaConfirmacionMs = opts.ventanaConfirmacionMs ?? VENTANA_CONFIRMACION_MS_DEFAULT;
  }

  /**
   * Interpreta la intención del usuario sobre el contexto → {@link Plan}
   * (R6.1–R6.9). Lee únicamente del `contexto` (R6.5) y de los Insights del
   * proveedor inyectado para las consultas de causa raíz (R6.2). Determinista.
   */
  async interpret(texto: string, contexto: ContextoIA): Promise<Result<Plan>> {
    if (typeof texto !== "string" || texto.trim().length === 0) {
      return { ok: false, error: "La intención del usuario es obligatoria para interpretar.", degraded: false };
    }
    if (!contexto || !Array.isArray(contexto.entidades)) {
      return { ok: false, error: "El ContextoIA es obligatorio y debe provenir del Motor_Contexto_IA (R6.5).", degraded: false };
    }

    let insights: Insight[] = [];
    if (clasificarIntencion(texto) === "causa_raiz") {
      try {
        insights = await this.insightsProvider();
      } catch {
        insights = [];
      }
    }

    const plan = interpretarPlan(texto, contexto, { insights });
    return { ok: true, value: plan };
  }

  /**
   * Propone las acciones de una operación multi-entidad **sin invocar** ninguna
   * Accion_Semantica (R6.4). Crea una propuesta pendiente con instante de
   * expiración (default 300 s, R6.10) que debe confirmarse explícitamente con
   * {@link CopilotLocal.confirmar}. Rechaza planes que no sean multi-entidad.
   */
  async proposeActions(plan: Plan): Promise<Result<PropuestaPendiente>> {
    if (plan.tipo !== "AccionMultiEntidad") {
      return { ok: false, error: `El plan '${plan.tipo}' no propone acciones multi-entidad.`, degraded: false };
    }
    const acciones = plan.acciones ?? [];
    if (acciones.length === 0) {
      return { ok: false, error: "El plan multi-entidad no contiene acciones que proponer.", degraded: false };
    }

    const ahora = this.reloj();
    const propuesta_id = generarId("prop");
    const resueltas: AccionResuelta[] = acciones.map((accion) => ({ accion, params: paramsDeAccion(accion) }));
    const expira_ms = ahora + this.ventanaConfirmacionMs;

    this.propuestas.set(propuesta_id, {
      propuesta_id,
      acciones: resueltas,
      explainability: plan.explainability,
      creada_ms: ahora,
      expira_ms,
      estado: "pendiente",
    });

    return {
      ok: true,
      value: {
        propuesta_id,
        acciones,
        expira_en: isoDe(expira_ms),
        explainability: plan.explainability,
      },
    };
  }

  /**
   * Confirma explícitamente una propuesta pendiente y ejecuta sus acciones
   * **vía el Registro_Acciones_Semanticas** (R6.4, R6.6): cada acción pasa por
   * el gate de permisos/políticas y HITL del Registro (`runtime.registry()`).
   * Si la propuesta no existe, ya fue resuelta o **expiró** (R6.10), se cancela
   * y se devuelve error sin invocar nada.
   */
  async confirmar(propuesta_id: Id, actor: string): Promise<Result<ResultadoAccion[]>> {
    const propuesta = this.propuestas.get(propuesta_id);
    if (!propuesta) {
      return { ok: false, error: `Propuesta '${propuesta_id}' no encontrada.`, degraded: false };
    }

    // Expiración perezosa por timestamp (R6.10): nunca invoca una caducada.
    if (propuesta.estado === "pendiente" && this.reloj() >= propuesta.expira_ms) {
      propuesta.estado = "expirada";
    }
    if (propuesta.estado !== "pendiente") {
      return {
        ok: false,
        error: `La propuesta '${propuesta_id}' no está pendiente (estado: ${propuesta.estado}); no se invoca ninguna acción (R6.10).`,
        degraded: false,
      };
    }

    propuesta.estado = "confirmada";
    const registry = runtime.registry();
    const resultados: ResultadoAccion[] = [];
    for (const { accion, params } of propuesta.acciones) {
      const res = await registry.invoke(accion.accion_semantica_id, params, actor);
      if (res.ok) resultados.push(res.value);
      else resultados.push({ estado: "denegada", detalle: res.error });
    }
    return { ok: true, value: resultados };
  }

  /** Cancela una propuesta pendiente sin invocar acciones (preserva estado). */
  cancelar(propuesta_id: Id): boolean {
    const propuesta = this.propuestas.get(propuesta_id);
    if (!propuesta || propuesta.estado !== "pendiente") return false;
    propuesta.estado = "cancelada";
    return true;
  }

  /**
   * Expira **todas** las propuestas pendientes cuya ventana ya venció según el
   * reloj inyectado (R6.10), sin invocar ninguna acción. Devuelve los ids
   * expirados. Pensada para un chequeo periódico o para los tests.
   */
  expirarPendientes(): Id[] {
    const ahora = this.reloj();
    const expirados: Id[] = [];
    for (const p of this.propuestas.values()) {
      if (p.estado === "pendiente" && ahora >= p.expira_ms) {
        p.estado = "expirada";
        expirados.push(p.propuesta_id);
      }
    }
    return expirados;
  }

  /** Estado actual de una propuesta (UI/test). */
  estadoPropuesta(propuesta_id: Id): PropuestaInterna["estado"] | undefined {
    return this.propuestas.get(propuesta_id)?.estado;
  }
}

// ---------------------------------------------------------------------------
// Resolución de rutas de navegación por tipo de entidad (UI)
// ---------------------------------------------------------------------------

/** Devuelve la ruta del SPA en la que enfocar una entidad navegable (R6.1). */
export function rutaParaEntidad(ref: EntidadRef): string {
  switch (ref.tipo) {
    case "DMA":
      return `/mapa?dma=${encodeURIComponent(ref.id)}`;
    case "Caso":
      return "/casos";
    case "Valvula":
      return "/valvulas";
    case "PRV":
      return "/prvs";
    case "Cuadrilla":
      return "/cuadrillas";
    case "Gateway":
      return "/lora";
    case "Medidor":
      return "/dmas";
    case "Anomalia":
    case "Alerta":
      return "/alertas";
    case "Orden_Inspeccion":
      return "/inspector/ordenes";
    case "Presion":
      return "/presion";
    case "Insight":
      return "/insights";
    default:
      return "/mapa";
  }
}

// ---------------------------------------------------------------------------
// Orquestación de UI: contexto (Motor_Contexto_IA) → Plan → presentación
// ---------------------------------------------------------------------------

/** Respuesta presentable del Copilot para el drawer de la UI. */
export interface RespuestaCopilotUI {
  /** Plan determinista producido por el planner. */
  plan: Plan;
  /** Texto principal de la respuesta. */
  answer: string;
  /** Viñetas de detalle (evidencia, entidades, acciones). */
  bullets: string[];
  /** Enlaces de drill-down a las vistas relevantes. */
  refs: Array<{ label: string; to: string }>;
  /** Propuesta pendiente cuando el plan es multi-entidad (R6.4). */
  propuesta?: PropuestaPendiente;
}

/** Proveedor de Insights inyectable para la orquestación de UI (tests). */
type InsightsLoader = () => Promise<Insight[]>;

let insightsLoaderUI: InsightsLoader | null = null;

/**
 * Permite a la capa de UI (o a los tests) registrar el cargador de Insights
 * usado para las consultas de causa raíz, evitando un acoplamiento directo de
 * este módulo con el loader de datos reales.
 */
export function configurarInsightsLoader(loader: InsightsLoader | null): void {
  insightsLoaderUI = loader;
}

/**
 * Orquesta una consulta del Copilot para la UI: ensambla el contexto **vía el
 * Motor_Contexto_IA** (`runtime.contextoIA()`, única puerta de lectura, R6.5),
 * interpreta el Plan vía `runtime.copilot()` y lo traduce a una respuesta
 * presentable. Para planes multi-entidad genera la {@link PropuestaPendiente}
 * sin invocar acciones (R6.4).
 */
export async function ejecutarConsultaCopilot(texto: string, usuario: string): Promise<RespuestaCopilotUI> {
  // 1) Contexto vía el Motor_Contexto_IA (única vía de lectura, R6.5).
  const ctxRes = await runtime.contextoIA().assemble(texto, usuario);
  const contexto: ContextoIA = ctxRes.ok
    ? ctxRes.value
    : { entidades: [], memoria: [], chunks: [], fuentes: [] };

  // 2) Insights para causa raíz, si hay loader configurado (R6.2).
  if (insightsLoaderUI && clasificarIntencion(texto) === "causa_raiz") {
    try {
      const insights = await insightsLoaderUI();
      // Reusa el planner puro directamente para inyectar los Insights cargados.
      const plan = interpretarPlan(texto, contexto, { insights });
      return await presentar(plan, usuario);
    } catch {
      /* degradación elegante: continúa por el planner del servicio */
    }
  }

  // 3) Plan vía el servicio Copilot del runtime.
  const planRes = await runtime.copilot().interpret(texto, contexto);
  if (!planRes.ok) {
    return {
      plan: {
        tipo: "Aclarar",
        aclaracion: "No pude interpretar la consulta. ¿Puedes reformularla?",
        explainability: { entidades_consultadas: [], evidencia: [], razonamiento: planRes.error },
      },
      answer: "No pude interpretar la consulta. ¿Puedes reformularla?",
      bullets: [],
      refs: [],
    };
  }

  return await presentar(planRes.value, usuario);
}

/** Traduce un Plan a la respuesta presentable de la UI (R6.8). */
async function presentar(plan: Plan, usuario: string): Promise<RespuestaCopilotUI> {
  const ex = plan.explainability;

  switch (plan.tipo) {
    case "NavegarEntidad": {
      const ref = plan.entidad!;
      return {
        plan,
        answer: `Enfocando ${ref.tipo} ${ref.id}.`,
        bullets: [ex.razonamiento],
        refs: [{ label: `Ver ${ref.tipo} ${ref.id}`, to: rutaParaEntidad(ref) }],
      };
    }

    case "ResponderCausaRaiz": {
      const sinInsight = ex.evidencia.length === 0;
      const answer = sinInsight
        ? "No existe un Insight aplicable para esa consulta de causa raíz. No voy a fabricar una explicación; revisa Operational Insights cuando haya correlaciones fundamentadas."
        : ex.razonamiento;
      const bullets = sinInsight
        ? []
        : [
            ...ex.evidencia.slice(0, 5).map((e) => `Evidencia citada: ${e.tipo} ${e.id}`),
            ...ex.entidades_consultadas.slice(0, 5).map((e) => `Entidad consultada: ${e.tipo} ${e.id}`),
          ];
      return {
        plan,
        answer,
        bullets,
        refs: [{ label: "Operational Insights", to: "/insights" }],
      };
    }

    case "ConsultaGeoOperacional": {
      const acciones = plan.acciones ?? [];
      return {
        plan,
        answer: ex.razonamiento,
        bullets: acciones.slice(0, 8).map((a) => a.razonamiento),
        refs: [
          { label: "Ver válvulas", to: "/valvulas" },
          { label: "Mapa operacional", to: "/mapa" },
        ],
      };
    }

    case "AccionMultiEntidad": {
      const propRes = await runtime.copilot().proposeActions(plan);
      const propuesta = propRes.ok ? propRes.value : undefined;
      const acciones = plan.acciones ?? [];
      return {
        plan,
        answer: `Propongo ${acciones.length} acción(es) sobre el conjunto objetivo. No se ejecutará nada hasta tu confirmación explícita${propuesta ? ` (expira ${propuesta.expira_en})` : ""}.`,
        bullets: acciones.slice(0, 10).map((a) => a.razonamiento),
        refs: [{ label: "Casos Prioritarios", to: "/casos" }],
        ...(propuesta ? { propuesta } : {}),
      };
    }

    case "Aclarar":
    default:
      return {
        plan,
        answer: plan.aclaracion ?? "¿Puedes aclarar tu solicitud?",
        bullets: ex.entidades_consultadas.map((e) => `Candidata: ${e.tipo} ${e.id}`),
        refs: [],
      };
  }
  // `usuario` se reserva para futuras rutas permission-aware en la presentación.
  void usuario;
}

// ---------------------------------------------------------------------------
// Autorregistro como proveedor DEMO del servicio `copilot`
// ---------------------------------------------------------------------------

let instancia: CopilotLocal | null = null;
let registrado = false;

/** Devuelve (creándolo si hace falta) el Copilot demo singleton. */
export function obtenerCopilot(): CopilotLocal {
  if (!instancia) instancia = new CopilotLocal();
  return instancia;
}

/**
 * Registra (una sola vez) la implementación DEMO del Copilot en el runtime
 * selector, de modo que las vistas lo consuman SIEMPRE vía `runtime.copilot()`
 * (R17.1, R17.2, R17.5) y nunca lo instancien directamente. Idempotente.
 */
export function registerCopilotRuntime(): void {
  if (registrado) return;
  registrado = true;
  provideDemo("copilot", () => obtenerCopilot());
}

// Registro temprano: al importar este módulo, `runtime.copilot()` resuelve al
// demo en lugar del stub "no implementado".
registerCopilotRuntime();
